import * as React from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import * as R from 'ramda';
import { IAppState } from '../../../core/redux/IAppState';
import { QuickEntitlement } from '../models';
import { isNilOrEmpty } from '../../../language/module';
import { getResource } from '../../../language/resources/getResource';
import { getValidLiftAccessLocationsAction, validLocationsKey } from './getValidLiftAccessLocationsAction';
import { LiftAccessValidLocationsEntry, locationDisplayName } from './liftAccessValidLocations';


export interface ValidLiftAccessLocationsView {
  /** Whether the location pin should be shown for a pass (hidden once loaded rules confirm no valid locations). */
  shouldShowLocationPin: (pass: QuickEntitlement) => boolean;
  /** The tooltip text for a restricted pass (loading / error / the sorted list of valid locations). */
  restrictionTextFor: (entitlementTypeId: number) => string;
}


/**
 * Loads and exposes the valid lift access locations for a guest's location-restricted passes.
 *
 * Fetches locations for restricted passes not yet cached, batched into a single call and de-duplicated across
 * guests (the cache is keyed by resort + entitlement type). Pin visibility is a pure function of the cached
 * rules: shown while loading/errored, hidden once the loaded rules confirm there are no valid locations.
 */
export const useValidLiftAccessLocations = (passes: QuickEntitlement[]): ValidLiftAccessLocationsView => {
  const dispatch = useDispatch();
  const resources = useSelector((state: IAppState) => state.locale);
  const resortAbbreviation = useSelector((state: IAppState) => state.selectedResort?.resortAbbreviation);

  const restrictedTypeIds = React.useMemo(
    () => R.uniq(passes.filter(pass => pass.isLocationRestricted).map(pass => pass.entitlementTypeId)),
    [passes],
  );

  // Keys we've already requested during this mount, so an errored key is retried at most once per mount rather
  // than on every render (a persistent error rewrites its cache entry, which would otherwise loop). Only read
  // inside the fetch effect, never during render, so it stays a safe dispatch guard.
  const requestedKeys = React.useRef<Set<string>>(new Set());

  // Subscribe only to the cached entries for THIS guest's restricted types at the current resort, compared
  // shallowly. A fetch for an unrelated guest/type produces a new cache map but leaves these entries' references
  // untouched, so this card doesn't re-render.
  const entriesByTypeId = useSelector((state: IAppState) => {
    const entries: Record<number, LiftAccessValidLocationsEntry> = {};
    if (!resortAbbreviation) {
      return entries;
    }
    restrictedTypeIds.forEach(typeId => {
      const entry = state.validLiftAccessLocations.get(validLocationsKey(resortAbbreviation, typeId));
      if (entry !== undefined) {
        entries[typeId] = entry;
      }
    });
    return entries;
  }, shallowEqual);

  // Fetch valid locations for restricted types we don't have cached yet, and retry any whose previous fetch
  // errored (retry-on-mount). Batched into a single call. The requestedKeys guard caps each key to one request
  // per mount so a persistent error can't cause a refetch loop; a fresh mount gets a fresh guard and retries.
  React.useEffect(() => {
    if (isNilOrEmpty(resortAbbreviation) || isNilOrEmpty(restrictedTypeIds)) {
      return;
    }

    const typeIdsToFetch = restrictedTypeIds.filter(typeId => {
      if (requestedKeys.current.has(validLocationsKey(resortAbbreviation!, typeId))) {
        return false;
      }
      const entry = entriesByTypeId[typeId];
      return entry === undefined || entry.status === 'error';
    });

    if (!isNilOrEmpty(typeIdsToFetch)) {
      typeIdsToFetch.forEach(typeId => requestedKeys.current.add(validLocationsKey(resortAbbreviation!, typeId)));
      dispatch(getValidLiftAccessLocationsAction(resortAbbreviation!, typeIdsToFetch));
    }
  }, [restrictedTypeIds, resortAbbreviation, entriesByTypeId, dispatch]);

  const shouldShowLocationPin = (pass: QuickEntitlement): boolean => {
    if (!pass.isLocationRestricted) {
      return false;
    }
    if (!resortAbbreviation) {
      return true;
    }

    const entry = entriesByTypeId[pass.entitlementTypeId];

    // Show optimistically while the rules are loading or errored; hide only once the loaded rules confirm the
    // pass has no valid locations (a stale flag).
    if (entry === undefined || entry.status === 'error') {
      return true;
    }
    return !isNilOrEmpty(entry.data.validLocations);
  };

  const restrictionTextFor = (entitlementTypeId: number): string => {
    const entry = entriesByTypeId[entitlementTypeId];

    if (!entry) {
      return getResource(resources, 'PASS_OVERVIEW_LOADING_VALID_LOCATIONS');
    }

    if (entry.status === 'error') {
      return getResource(resources, 'PASS_OVERVIEW_VALID_LOCATIONS_ERROR');
    }

    // Undisplayable locations are dropped rather than left as gaps in the comma-separated list.
    const locationNames = entry.data.validLocations
      .map(locationDisplayName)
      .filter(name => !isNilOrEmpty(name))
      .sort((first, second) => first.localeCompare(second));

    return isNilOrEmpty(locationNames)
      ? getResource(resources, 'PASS_OVERVIEW_NO_VALID_LOCATIONS')
      : `${getResource(resources, 'PASS_OVERVIEW_RESTRICTED_PREFIX')} ${locationNames.join(', ')}`;
  };

  return { shouldShowLocationPin, restrictionTextFor };
};
